import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GLOBAL_RESULTS_LIST_SIZE } from '../../redux/consts';
import { RootState } from '../../redux/store';
import { Conversation } from '../../Types/conversation';
import { Folder } from '../../Types/folder';
import { getGlobalSearchResults } from '../actions/globalSearchActions';

interface GlobalSearchState {
  folders: Folder[];
  conversations: Conversation[];
  currentCount: number;
  totalCount: number;
  searchQuery: string;
  searchSelected: boolean;
  advancedSearchSelected: boolean;
  page: number;
  conversationsOnly?: boolean;
  createdByMeOnly?: boolean;
  isGlobalSearching: boolean;
}

const initialState: GlobalSearchState = {
  folders: [],
  conversations: [],
  currentCount: 0,
  totalCount: 0,
  searchQuery: '',
  page: 1,
  searchSelected: false,
  advancedSearchSelected: false,
  conversationsOnly: undefined,
  createdByMeOnly: undefined,
  isGlobalSearching: false,
};

const globalSearchSlice = createSlice({
  name: 'globalSearch',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSearchSelected: (state, action: PayloadAction<boolean>) => {
      state.searchSelected = action.payload;
    },
    setAdvancedSearchSelected: (state, action: PayloadAction<boolean>) => {
      state.advancedSearchSelected = action.payload;
    },
    setShowConversationsOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.conversationsOnly = action.payload;
    },
    setCreatedByMeOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.createdByMeOnly = action.payload;
    },
    setIsGlobalSearching: (state, action: PayloadAction<boolean>) => {
      state.isGlobalSearching = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getGlobalSearchResults.fulfilled,
      (
        state,
        action: PayloadAction<{
          conversations?: Conversation[];
          folders?: Folder[];
          totalCount: number;
        }>
      ) => {
        const { conversations, folders } = action.payload;
        const pageOffset = GLOBAL_RESULTS_LIST_SIZE * (state.page - 1);
        if (pageOffset === 0) {
          state.conversations = conversations || [];
          state.folders = folders || [];
        } else {
          state.conversations = [...state.conversations, ...(conversations || [])];
          state.folders = [...state.folders, ...(folders || [])];
        }

        state.currentCount = state.conversations.length + state.folders.length;
        state.totalCount = action.payload.totalCount;
      }
    );
  },
});

export const {
  setSearchQuery,
  setPage,
  setIsGlobalSearching,
  setSearchSelected,
  setAdvancedSearchSelected,
  setShowConversationsOnly,
  setCreatedByMeOnly,
} = globalSearchSlice.actions;

export const selectGlobalSearch = (state: RootState) => state.globalSearch;

export default globalSearchSlice.reducer;
